/**
 * Splitting ONE amount of money across departments by percentage.
 *
 * WHY THIS IS ITS OWN MODULE. Four of Michael's five pay exceptions (2026-09-03) are
 * department splits: a salaried employee's pay divided across two to five departments,
 * and a commission divided between two PEOPLE. In every case the operation is the same
 * one — take a gross figure NetSuite computed, and re-state it as several lines that
 * must add back up to it EXACTLY.
 *
 * "Exactly" is the whole difficulty. 33.33% of $2,000.00 is $666.60, and three of those
 * is $1,999.80 — twenty cents of someone's salary that would simply disappear. Naive
 * rounding also goes the other way and pays out more than the paycheck holds, which
 * NetSuite will either reject or quietly accept as a discrepancy nobody notices until
 * the books are reconciled. So the shares are computed in CENTS and the remainder is
 * distributed deterministically, and the result is asserted to sum to the input.
 *
 * PURE and dependency-free, like {@link import('./overtime')} — the API computes the
 * lines and the browser previews them from this one implementation. A second copy of a
 * money-splitting rule is a second answer about someone's pay.
 */
/** Round to whole cents, away from zero on a .5 tie. */
function toCents(amount) {
    return Math.round(Math.abs(amount) * 100) * Math.sign(amount || 1);
}
/**
 * Validate a set of shares. Returns a human reason or null.
 *
 * The sum is checked in HUNDREDTHS OF A PERCENT — as integers — rather than by
 * comparing floats. Not because the realistic splits drift: 33.33 + 33.33 + 33.34
 * does sum to exactly 100 in double precision, and so does every other two-decimal
 * set tried. The reasons are the ones that survive that:
 *
 *   - it is exact BY CONSTRUCTION, so it cannot depend on which particular
 *     percentages happen to be representable — a tolerance would be a number
 *     someone has to justify, and integers need no tolerance;
 *   - it makes over-precise input FAIL rather than pass. 33.333 + 33.333 + 33.334
 *     also sums to 100, but numeric(5,2) would silently round each on save, so the
 *     stored split would not be the one that was reviewed. The two-decimal check
 *     below catches that, and the integer sum keeps the two checks consistent.
 */
export function validateAllocationShares(shares) {
    if (shares.length < 2) {
        return 'A split needs at least two departments.';
    }
    const seen = new Set();
    for (const s of shares) {
        if (seen.has(s.departmentId)) {
            return 'Each department can appear only once in a split.';
        }
        seen.add(s.departmentId);
        if (!Number.isFinite(s.percent) || s.percent <= 0 || s.percent > 100) {
            return 'Each share must be greater than 0 and at most 100%.';
        }
        // Two decimals is the column's precision; a third would be silently rounded
        // on save and the stored split would not be the one that was reviewed.
        if (Math.round(s.percent * 100) !== Number((s.percent * 100).toFixed(4))) {
            return 'A share can have at most two decimal places.';
        }
    }
    const hundredths = shares.reduce((sum, s) => sum + Math.round(s.percent * 100), 0);
    if (hundredths !== 10_000) {
        return `A split must total 100% (got ${(hundredths / 100).toFixed(2)}%).`;
    }
    return null;
}
/**
 * Split `gross` across `shares`, in cents, so the parts sum to `gross` EXACTLY.
 *
 * Uses the largest-remainder method: floor every share, then hand the leftover cents
 * one at a time to the shares with the largest discarded fraction. That is the
 * standard apportionment rule and it has the two properties that matter here —
 * the total is preserved, and no share is ever more than a cent from its ideal.
 *
 * TIES GO TO THE LARGER PERCENT, then to declaration order. Determinism is the point:
 * the same split must produce the same cents every pay period, or a salaried employee's
 * department costs wobble by a cent for no reason and reconciliation chases it. Where
 * an exact three-way split is impossible, Michael chose which department carries the
 * extra hundredth by ORDERING it last with the larger percent (33.34), so declaration
 * order and percent together already encode his decision.
 *
 * Callers should validate with {@link validateAllocationShares} first; this function
 * apportions whatever it is given rather than second-guessing it, so shares that do
 * not sum to 100 will faithfully split the gross in those proportions.
 */
export function allocateAmount(gross, shares) {
    const grossCents = toCents(gross);
    if (shares.length === 0) {
        return [];
    }
    const totalHundredths = shares.reduce((sum, s) => sum + Math.round(s.percent * 100), 0);
    if (totalHundredths === 0) {
        return shares.map((s) => ({ ...s, cents: 0, amount: 0 }));
    }
    // Ideal share in cents, kept as an exact integer ratio so no float creeps in:
    // grossCents * hundredths / totalHundredths.
    const scaled = shares.map((s, index) => {
        const numerator = grossCents * Math.round(s.percent * 100);
        const floor = Math.trunc(numerator / totalHundredths);
        return {
            index,
            share: s,
            cents: floor,
            remainder: numerator - floor * totalHundredths,
        };
    });
    let leftover = grossCents - scaled.reduce((sum, r) => sum + r.cents, 0);
    // Largest remainder first; ties to the larger percent, then declaration order.
    const order = [...scaled].sort((a, b) => {
        if (a.remainder !== b.remainder) {
            return b.remainder - a.remainder;
        }
        if (a.share.percent !== b.share.percent) {
            return b.share.percent - a.share.percent;
        }
        return a.index - b.index;
    });
    // `leftover` is negative when the gross is negative; step toward zero either way.
    const step = leftover >= 0 ? 1 : -1;
    for (let i = 0; leftover !== 0; i = (i + 1) % order.length) {
        const target = order[i];
        if (target !== undefined) {
            target.cents += step;
            leftover -= step;
        }
    }
    return scaled.map((r) => ({ ...r.share, cents: r.cents, amount: r.cents / 100 }));
}
/** Total of an allocation, in cents — for asserting it matches the gross. */
export function allocatedTotalCents(lines) {
    return lines.reduce((sum, l) => sum + l.cents, 0);
}
//# sourceMappingURL=pay-allocation.js.map